import React from 'react';
import { PostInsights } from '@frontend/gql/social/graphql';
import { useSocialMediaThumbnail } from '@frontend/applications/Social/hooks/useSocialMediaThumbnail';
import {
  PostCard,
  PostCardMedia,
  PostCardSkeleton,
  PostCardBackground,
  PostCardContent,
  PostCardImageContainer,
  PostCardImageContent,
  PostCardImage,
  PostCardPlaceholder,
  PostCardGradient,
  PostCardFooter,
} from '@frontend/applications/Social/components/post-card';
import {
  CardActionButtons,
  CardUsernameDisplay,
  CardBadgeSection,
  CardStatsSection,
} from '@frontend/shadcn/components/card-overlay';
import {
  useSimilarCreatorsActionButtons,
  useInstagramUsernameDisplay,
  useSimilarCreatorsBadges,
  useSimilarCreatorsStats,
} from '@frontend/shadcn/components/card-overlay/hooks';
import { useTagsDisplay } from '@/shadcn/components/content-preview/hooks/use-tags-display';
import { TagsDisplay } from '@/shadcn/components/content-preview/tags-display';

type SimilarCreatorsContentPreviewCardProps = {
  post: PostInsights;
  className?: string;
  onDownload?: () => void;
  onOpenExternal?: () => void;
  showDownload?: boolean;
};

const SimilarCreatorsContentPreviewCard = React.forwardRef<HTMLDivElement, SimilarCreatorsContentPreviewCardProps>(
  ({ post, className, onDownload, onOpenExternal, showDownload = true, ...props }, ref) => {
    const total = post.total;
    const { tags, tagsVisible } = useTagsDisplay(post.postCaption);
    const { imageUrl, isLoading, onImageError } = useSocialMediaThumbnail({
      postUrl: post.postPermalink ? post.postPermalink : '',
      postImageUrl: post.postImageUrl ? post.postImageUrl : '',
    });

    const showLoading = isLoading;
    const showImage = imageUrl && !showLoading;
    const showPlaceholder = !showImage && !showLoading;

    // Get username for display
    const username = post.accountUsername || post.member?.instagramUsername;

    // Check if boost eligible
    const isBoostEligible = post.advertisableMediaPermissions?.some(
      (permission) => permission?.hasPermissions
    );

    // Use hooks to build component props
    const { cardActionButtonsProps, hasActions } = useSimilarCreatorsActionButtons(
      onOpenExternal,
      onDownload,
      showDownload
    );

    const { cardUsernameDisplayProps, shouldDisplay: shouldDisplayUsername } = useInstagramUsernameDisplay(username);

    const { cardBadgeSectionProps, hasBadges } = useSimilarCreatorsBadges(isBoostEligible);

    const { cardStatsSectionProps, hasStats } = useSimilarCreatorsStats(
      total?.impressions, // Using impressions as followers count
      total?.engagements
    );

    return (
      <PostCard ref={ref} className={className} {...props}>
        <PostCardMedia>
          {showLoading && <PostCardSkeleton />}
          {showImage && (
            <>
              <PostCardBackground imageUrl={imageUrl} />
              <PostCardImageContainer>
                <PostCardImageContent>
                  <PostCardImage src={imageUrl} onError={onImageError} />
                </PostCardImageContent>
              </PostCardImageContainer>
            </>
          )}
          {showPlaceholder && <PostCardPlaceholder />}
          <PostCardGradient />
        </PostCardMedia>

        <PostCardContent>
          {/* Top Action Buttons */}
          {hasActions && <CardActionButtons {...cardActionButtonsProps} />}

          {/* Username Display */}
          {shouldDisplayUsername && cardUsernameDisplayProps && (
            <CardUsernameDisplay {...cardUsernameDisplayProps} />
          )}

          <div className="flex-grow" />

          <PostCardFooter>
            {/* Boost Eligible Badge */}
            {hasBadges && <CardBadgeSection {...cardBadgeSectionProps} />}

            {/* Tags Row - only show if post has caption */}
            <TagsDisplay tags={tags} tagsVisible={tagsVisible} />

            {/* Stats Row - No Date for Similar Creators */}
            {hasStats && <CardStatsSection {...cardStatsSectionProps} />}
          </PostCardFooter>
        </PostCardContent>
      </PostCard>
    );
  },
);

SimilarCreatorsContentPreviewCard.displayName = 'SimilarCreatorsContentPreviewCard';

export { SimilarCreatorsContentPreviewCard };
